
import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  Box,
  Button,
  Input,
  Typography,
  Stack,
  CircularProgress,
  Grid,
  Card,
  Avatar,
  Chip,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Alert,
  List,
  ListItem,
  ListItemContent
} from '@mui/joy';
import PageLayout from '../components/PageLayout';
import MetricCard from '../components/MetricCard';
import EnhancedButton from '../components/EnhancedButton';
import EnhancedInput from '../components/EnhancedInput';
import LoadingSkeleton, { CardSkeleton } from '../components/LoadingSkeleton';

export default function UserDetail() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [showAlert, setShowAlert] = useState(false);
  const [userStats, setUserStats] = useState({
    projectsCount: 0,
    documentsCount: 0,
    lastLogin: null,
    accountAge: 0
  });

  // Helper functions
  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(word => word[0]).join('').toUpperCase();
  };

  const showAlertMessage = (message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  };

  const calculateAccountAge = (createdAt) => {
    if (!createdAt) return 0;
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - created);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      // Fetch user profile
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (!error && data) {
        setProfile(data);
        
        // Calculate account age
        const accountAge = calculateAccountAge(data.created_at);
        
        // Fetch user statistics (projects, documents)
        const [projectsResponse, documentsResponse] = await Promise.all([
          supabase.from('projects').select('id').eq('created_by', user.id),
          supabase.from('internal_documents').select('id').eq('uploaded_by', user.id)
        ]);

        setUserStats({
          projectsCount: projectsResponse.data?.length || 0,
          documentsCount: documentsResponse.data?.length || 0,
          lastLogin: data.last_sign_in_at || data.created_at,
          accountAge
        });
      }
      
      setLoading(false);
    };
    
    fetchProfile();
  }, []);

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase.from('users').update({
      display_name: profile.display_name,
      job_title: profile.job_title,
      department: profile.department,
      phone: profile.phone,
      notification: profile.notification,
    }).eq('id', profile.id);
    setSaving(false);
    
    if (!error) {
      showAlertMessage('Profile updated successfully!', 'success');
    } else {
      showAlertMessage('Error updating profile: ' + error.message, 'danger');
      console.error('Error updating profile:', error);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword.length < 6) {
      showAlertMessage('Password must be at least 6 characters long', 'warning');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      showAlertMessage('Passwords do not match', 'warning');
      return;
    }
    
    setPwSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setPwSaving(false);
    
    if (error) {
      showAlertMessage('Error updating password: ' + error.message, 'danger');
    } else {
      showAlertMessage('Password updated successfully!', 'success');
      setNewPassword('');
      setConfirmPassword('');
    }
  };


  if (loading || !profile) {
    return (
      <PageLayout title="My Profile" fullWidth>
        <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 3 }}>
          {/* Profile Header Loading */}
          <Box sx={{ mb: 4 }}>
            <LoadingSkeleton height="40px" width="200px" sx={{ mb: 3 }} />
            <Card sx={{ p: 4 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid xs="auto">
                  <LoadingSkeleton width="120px" height="120px" variant="circular" />
                </Grid>
                <Grid xs>
                  <LoadingSkeleton height="32px" width="250px" sx={{ mb: 2 }} />
                  <LoadingSkeleton height="20px" width="180px" sx={{ mb: 1 }} />
                  <LoadingSkeleton height="16px" width="200px" />
                </Grid>
              </Grid>
            </Card>
          </Box>
          
          {/* Stats Loading */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          </Grid>
          
          {/* Content Loading */}
          <CardSkeleton />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="My Profile" fullWidth>
      <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 3 }}>
        {/* Alert Messages */}
        {showAlert && (
          <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
            <Alert
              color={alertType}
              onClose={() => setShowAlert(false)}
              sx={{ mb: 2 }}
            >
              {alertMessage}
            </Alert>
          </Box>
        )}

        {/* Profile Header */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            level="h3" 
            sx={{ 
              mb: 3, 
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
            className="fade-in"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '32px', fontFamily: 'Material Symbols Outlined' }}>
              account_circle
            </span>
            My Profile
          </Typography>
          
          <Card 
            sx={{ 
              p: 4,
              background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
              border: '1px solid',
              borderColor: 'success.200',
            }}
            className="fade-in-up"
          >
            <Grid container spacing={3} alignItems="center">
              <Grid xs="auto">
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    size="lg"
                    sx={{
                      width: 120,
                      height: 120,
                      bgcolor: '#00A884',
                      color: 'white',
                      fontSize: '2.5rem',
                      fontWeight: 700,
                    }}
                  >
                    {getInitials(profile.display_name || profile.email)}
                  </Avatar>
                </Box>
              </Grid>
              
              <Grid xs>
                <Typography level="h4" sx={{ color: 'success.700', mb: 1 }}>
                  {profile.display_name || 'User'}
                </Typography>
                <Typography level="body-lg" sx={{ color: 'text.secondary', mb: 1 }}>
                  {profile.job_title || 'No job title set'} {profile.department && `• ${profile.department}`}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.tertiary', mb: 2 }}>
                  📧 {profile.email}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip variant="soft" color="success">
                    {profile.user_type || 'User'}
                  </Chip>
                  <Chip variant="soft" color="neutral">
                    Member for {userStats.accountAge} days
                  </Chip>
                  <Chip variant="soft" color="primary">
                    Last login: {new Date(userStats.lastLogin).toLocaleDateString()}
                  </Chip>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Box>

        {/* User Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Projects"
              value={userStats.projectsCount}
              icon="folder"
              gradientColors={['#00A884', '#007E33']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Documents"
              value={userStats.documentsCount}
              icon="description"
              gradientColors={['#17a2b8', '#138496']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Account Age"
              value={`${userStats.accountAge}d`}
              icon="calendar_today"
              gradientColors={['#28a745', '#218838']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Profile Status"
              value="Active"
              icon="verified"
              gradientColors={['#6c757d', '#5a6268']}
            />
          </Grid>
        </Grid>

        {/* Profile Content Tabs */}
        <Card sx={{ overflow: 'hidden' }} className="fade-in-up">
          <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)}>
            <TabList sx={{ bgcolor: 'success.50' }}>
              <Tab sx={{ gap: 1 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', fontFamily: 'Material Symbols Outlined' }}>
                  edit
                </span>
                Edit Profile
              </Tab>
              <Tab sx={{ gap: 1 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', fontFamily: 'Material Symbols Outlined' }}>
                  security
                </span>
                Security
              </Tab>
              <Tab sx={{ gap: 1 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', fontFamily: 'Material Symbols Outlined' }}>
                  history
                </span>
                Activity
              </Tab>
            </TabList>

            {/* Edit Profile Tab */}
            <TabPanel value={0} sx={{ p: 4 }}>
              <Typography level="h4" sx={{ mb: 3, color: 'success.700' }}>
                Personal Information
              </Typography>
              
              <Grid container spacing={3}>
                <Grid xs={12}>
                  <EnhancedInput
                    label="Email Address"
                    value={profile.email || ''}
                    disabled
                    helperText="Email cannot be changed"
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                        email
                      </span>
                    }
                  />
                </Grid>
                
                <Grid xs={12} sm={6}>
                  <EnhancedInput
                    label="Display Name"
                    value={profile.display_name || ''}
                    onChange={(e) => handleChange('display_name', e.target.value)}
                    placeholder="Enter your display name"
                    required
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                        person
                      </span>
                    }
                  />
                </Grid>
                
                <Grid xs={12} sm={6}>
                  <EnhancedInput
                    label="Job Title"
                    value={profile.job_title || ''}
                    onChange={(e) => handleChange('job_title', e.target.value)}
                    placeholder="Enter your job title"
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                        work
                      </span>
                    }
                  />
                </Grid>
                
                <Grid xs={12} sm={6}>
                  <EnhancedInput
                    label="Department"
                    value={profile.department || ''}
                    onChange={(e) => handleChange('department', e.target.value)}
                    placeholder="Enter your department"
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                        business
                      </span>
                    }
                  />
                </Grid>
                
                <Grid xs={12} sm={6}>
                  <EnhancedInput
                    label="Phone Number"
                    value={profile.phone || ''}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="Enter your phone number"
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                        phone
                      </span>
                    }
                  />
                </Grid>
                
                <Grid xs={12}>
                  <EnhancedInput
                    label="Notification Preference"
                    value={profile.notification || ''}
                    onChange={(e) => handleChange('notification', e.target.value)}
                    placeholder="Email, SMS, Both, or None"
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                        notifications
                      </span>
                    }
                  />
                </Grid>
                
                <Grid xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <EnhancedButton
                      gradient
                      onClick={handleSave}
                      loading={saving}
                      startDecorator={
                        <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                          save
                        </span>
                      }
                    >
                      Save Changes
                    </EnhancedButton>
                  </Box>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Security Tab */}
            <TabPanel value={1} sx={{ p: 4 }}>
              <Typography level="h4" sx={{ mb: 3, color: 'success.700' }}>
                Security Settings
              </Typography>
              
              <Card sx={{ p: 3, mb: 3, bgcolor: 'warning.25', border: '1px solid', borderColor: 'warning.200' }}>
                <Typography level="title-sm" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    security
                  </span>
                  Password Requirements
                </Typography>
                <Typography level="body-sm">
                  • Minimum 6 characters long<br/>
                  • Contains both letters and numbers<br/>
                  • Avoid common words or personal information
                </Typography>
              </Card>
              
              <Grid container spacing={3}>
                <Grid xs={12} sm={6}>
                  <EnhancedInput
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                        lock
                      </span>
                    }
                    helperText={newPassword && newPassword.length < 6 ? 'Password must be at least 6 characters' : ''}
                    error={newPassword && newPassword.length < 6}
                  />
                </Grid>
                
                <Grid xs={12} sm={6}>
                  <EnhancedInput
                    label="Confirm New Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                        lock
                      </span>
                    }
                    helperText={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
                    error={confirmPassword && newPassword !== confirmPassword}
                  />
                </Grid>
                
                <Grid xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <EnhancedButton
                      variant="outlined"
                      onClick={() => {
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                    >
                      Cancel
                    </EnhancedButton>
                    <EnhancedButton
                      gradient
                      onClick={handlePasswordChange}
                      loading={pwSaving}
                      disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword || newPassword.length < 6}
                      startDecorator={
                        <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                          key
                        </span>
                      }
                    >
                      Update Password
                    </EnhancedButton>
                  </Box>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Activity Tab */}
            <TabPanel value={2} sx={{ p: 4 }}>
              <Typography level="h4" sx={{ mb: 3, color: 'success.700' }}>
                Recent Activity
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography level="title-sm" sx={{ mb: 2 }}>Account Information</Typography>
                <List>
                  <ListItem>
                    <ListItemContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined', color: '#00A884' }}>
                          person_add
                        </span>
                        <Box>
                          <Typography level="title-sm">Account Created</Typography>
                          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            {new Date(profile.created_at).toLocaleDateString()} - Welcome to FLD&P Grid!
                          </Typography>
                        </Box>
                      </Box>
                    </ListItemContent>
                  </ListItem>
                  
                  <ListItem>
                    <ListItemContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined', color: '#17a2b8' }}>
                          login
                        </span>
                        <Box>
                          <Typography level="title-sm">Last Login</Typography>
                          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            {new Date(userStats.lastLogin).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItemContent>
                  </ListItem>
                  
                  <ListItem>
                    <ListItemContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined', color: '#28a745' }}>
                          folder
                        </span>
                        <Box>
                          <Typography level="title-sm">Projects</Typography>
                          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            You have {userStats.projectsCount} projects in the system
                          </Typography>
                        </Box>
                      </Box>
                    </ListItemContent>
                  </ListItem>
                  
                  <ListItem>
                    <ListItemContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined', color: '#6c757d' }}>
                          description
                        </span>
                        <Box>
                          <Typography level="title-sm">Documents</Typography>
                          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            You have uploaded {userStats.documentsCount} documents
                          </Typography>
                        </Box>
                      </Box>
                    </ListItemContent>
                  </ListItem>
                </List>
              </Box>
              
              <Card sx={{ p: 3, bgcolor: 'success.25', border: '1px solid', borderColor: 'success.200' }}>
                <Typography level="title-sm" sx={{ mb: 1 }}>
                  🎉 You're doing great!
                </Typography>
                <Typography level="body-sm">
                  Keep up the excellent work managing your projects and staying organized with FLD&P Grid.
                </Typography>
              </Card>
            </TabPanel>
          </Tabs>
        </Card>

      </Box>
    </PageLayout>
  );
}